/**
 * Date utilities for GT L.A.B Careers
 * Treats all pay-period/hire/birthday dates as timezone-agnostic calendar dates
 */

/**
 * Format a date string as calendar date (no timezone shifts)
 * Input: "2026-06-28" or "2026-06-28T00:00:00Z"
 * Output: "Jun 28, 2026"
 */
export function formatDate(dateString, options = {}) {
  if (!dateString) return null;
  
  // Extract YYYY-MM-DD from ISO string
  const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) return null;
  
  const [, year, month, day] = dateMatch;
  
  // Create date using UTC accessors to avoid timezone shift
  const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
  
  const defaultOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  };
  
  return date.toLocaleDateString("en-US", { ...defaultOptions, ...options });
}

/**
 * Format date range (period display)
 * Input: "2026-06-28", "2026-07-11"
 * Output: "Jun 28 – Jul 11, 2026"
 */
export function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return null;
  
  const start = formatDate(startDate, { year: undefined });
  const end = formatDate(endDate, { month: "short", day: "numeric", year: "numeric" });
  
  return `${start} – ${end}`;
}

/**
 * Format birthday (month and day only, no year)
 * Input: "1990-03-15" or "1990-03-15T00:00:00Z"
 * Output: "March 15"
 */
export function formatBirthday(dateString) {
  if (!dateString) return null;
  
  return formatDate(dateString, { month: "long", day: "numeric", year: undefined });
}

/**
 * Get current date as YYYY-MM-DD (calendar date, not timestamp)
 */
export function getCurrentDate() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse date to components (year, month, day)
 */
export function parseDateComponents(dateString) {
  if (!dateString) return null;
  
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  
  return {
    year: parseInt(match[1]),
    month: parseInt(match[2]),
    day: parseInt(match[3])
  };
}
